import React, { ChangeEvent, useEffect, useRef } from 'react';
import { Drawer, Button, Input, Form, Image, Select } from 'antd';
import { CloseOutlined, FileImageOutlined } from '@ant-design/icons';
import {
	uploadLogotype,
	addMultiSelect,
	removeMultiSelect,
	changeOrganization,
	closeModal,
	getOrganizationById,
	removeLogotype,
	EditOrganizationModalSelector,
} from './EditOrganizationModalSlice';
import * as S from './EditOrganizationModal.styled';
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { IComplex, IUser, ILot } from 'interfaces/main';
import { exchangeOrganization } from 'features/OrganizationsList/OrganizationsListSlice';
import { unwrapResult } from '@reduxjs/toolkit';
import { ComplexesListItemsSelector } from 'features/ComplexesList/ComplexesListSlice';
import Spinner from 'components/Spinner';

const layout = {
	labelCol: { span: 6 },
	wrapperCol: { span: 18 },
};

const { TextArea } = Input;
const { Option } = Select;

const EditOrganizationModal: React.FC = () => {
	const dispatch = useAppDispatch();

	const [form] = Form.useForm();

	const logotypeInputRef = useRef<HTMLInputElement | null>(null);

	// Данные организации
	const { data, isLoading, isLogotypeLoading, isVisible } = useAppSelector(
		EditOrganizationModalSelector,
	);

	// Доступные ЖК
	const allComplexes = useAppSelector(ComplexesListItemsSelector);

	const {
		id,
		title,
		description_en,
		description_ru,
		description_de,
		email,
		phone,
		telegram,
		facebook,
		whatsapp,
		site,
		needBorder,
		logotype,
		complexes,
		lots,
		users,
	} = data;

	// Получение данных организации
	useEffect(() => {
		if (id)
			dispatch(getOrganizationById({ id }))
				.then(unwrapResult)
				.then((fetchedOrganization) => {
					const o = {
						...fetchedOrganization,
						complexes: fetchedOrganization.complexes.map((item: IComplex) => item.id),
						lots: fetchedOrganization.lots.map((item: ILot) => item.id),
						users: fetchedOrganization.users.map((item: IUser) => item.id),
					};

					const fields = Object.keys(o).map((fieldName: string) => ({
						name: fieldName,
						value: o[fieldName],
					}));

					form.setFields(fields);
				});
	}, [dispatch, form, id]);

	// Закрытие окна
	const onClose = () => {
		form.submit();
	};

	// Загрузка логотипа
	const onUploadLogotype = (event: ChangeEvent<HTMLInputElement>) => {
		let newFile = event.target.files[0];

		if (!!newFile) {
			dispatch(uploadLogotype({ file: newFile, organizationId: id }));
		}
	};

	const onUploadLogotypeClick = () => {
		logotypeInputRef.current?.click();
	};

	const onSubmit = () => {
		dispatch(exchangeOrganization(data));
		dispatch(closeModal());
	};

	const onRemoveLogotype = () => {
		dispatch(removeLogotype(logotype.id));
	};

	// Ошибки в форме при сабмите
	const onError = (errors) => console.log(errors);

	// Изменение основных полей организации
	const onChangeOrganization = (key: string, value: string | number | boolean) =>
		dispatch(changeOrganization({ key, organizationId: id, value }));

	// Изменение текстового поля
	const onChangeTextField = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
		onChangeOrganization(event.target.name, event.target.value);

	// Изменение логического поля
	const onChangeBooleanField = (name: string, value: boolean) => onChangeOrganization(name, value);

	// Добавление в мультиселектах
	const onAddMultiSelect = (objectId: number, key: 'complexes' | 'lots' | 'users') =>
		dispatch(addMultiSelect({ objectId, organizationId: id, key }));

	// Добавление в мультиселектах
	const onRemoveMultiSelect = (objectId: number, key: 'complexes' | 'lots' | 'users') =>
		dispatch(removeMultiSelect({ objectId, organizationId: id, key }));

	return (
		<Drawer
			title="Edit organization"
			placement="right"
			width={624}
			onClose={onClose}
			open={isVisible}>
			{(isLogotypeLoading || isLoading) && <Spinner />}
			<Form
				{...layout}
				name="editOrganizationForm"
				id="editOrganizationForm"
				form={form}
				initialValues={{
					title,
					description_en,
					description_ru,
					description_de,
					email,
					phone,
					telegram,
					facebook,
					whatsapp,
					site,
					needBorder,
					complexes: complexes.map((item: IComplex) => item.id),
					lots: lots.map((item: ILot) => item.id),
					users: users.map((item: IUser) => item.id),
				}}
				onFinish={onSubmit}
				onError={onError}>
				<Form.Item
					name="title"
					label="Title"
					rules={[{ required: true, message: "Please input Organization's title!" }]}>
					<Input type="text" placeholder="zDegree DIP" name="title" onChange={onChangeTextField} />
				</Form.Item>
				<Form.Item name="description_en" label="Description (English)">
					<TextArea
						style={{ width: '100%' }}
						rows={3}
						placeholder="129, 318 Roud, Al Quoz Fourth - Dubai"
						maxLength={1000}
						name="description_en"
						onChange={onChangeTextField}
					/>
				</Form.Item>
				<Form.Item name="description_ru" label="Description (Russian)">
					<TextArea
						style={{ width: '100%' }}
						rows={3}
						placeholder="129, 318 Roud, Al Quoz Fourth - Dubai"
						maxLength={1000}
						name="description_ru"
						onChange={onChangeTextField}
					/>
				</Form.Item>
				<Form.Item name="description_de" label="Description (Deutsch)">
					<TextArea
						style={{ width: '100%' }}
						rows={3}
						placeholder="129, 318 Roud, Al Quoz Fourth - Dubai"
						maxLength={1000}
						name="description_de"
						onChange={onChangeTextField}
					/>
				</Form.Item>
				<Form.Item name="email" label="Email">
					<Input
						type="text"
						placeholder="info@maksimzaycev.ru"
						name="email"
						onChange={onChangeTextField}
					/>
				</Form.Item>
				<Form.Item name="phone" label="Phone">
					<Input
						type="text"
						placeholder="+971 4 3381551"
						name="phone"
						onChange={onChangeTextField}
					/>
				</Form.Item>
				<Form.Item name="telegram" label="Telegram">
					<Input
						type="text"
						placeholder="@frontend_max"
						name="telegram"
						onChange={onChangeTextField}
					/>
				</Form.Item>
				<Form.Item name="facebook" label="Facebook">
					<Input
						type="text"
						placeholder="maksim.zaycev"
						name="facebook"
						onChange={onChangeTextField}
					/>
				</Form.Item>
				<Form.Item name="whatsapp" label="WhatsApp">
					<Input
						type="text"
						placeholder="+971 4 3381551"
						name="whatsapp"
						onChange={onChangeTextField}
					/>
				</Form.Item>
				<Form.Item name="site" label="Site">
					<Input
						type="text"
						placeholder="www.maksimzaycev.ru"
						name="site"
						onChange={onChangeTextField}
					/>
				</Form.Item>
				<Form.Item name="needBorder" label="Need border">
					<Select
						style={{ width: '100%' }}
						placeholder="Please select"
						onChange={(value: boolean) => onChangeBooleanField('needBorder', value)}>
						<Option value={true}>Yes</Option>
						<Option value={false}>No</Option>
					</Select>
				</Form.Item>
				<Form.Item name="complexes" label="Living complexes">
					<Select
						mode="multiple"
						disabled
						style={{ width: '100%' }}
						placeholder="Please select"
						onSelect={(key: number) => onAddMultiSelect(key, 'complexes')}
						onDeselect={(key: number) => onRemoveMultiSelect(key, 'complexes')}>
						{allComplexes.map((item: IComplex) => (
							<Option key={item.id} value={item.id}>
								{item.title}
							</Option>
						))}
					</Select>
				</Form.Item>
				<Form.Item name="lots" label="Lots">
					<Select
						mode="multiple"
						disabled
						style={{ width: '100%' }}
						placeholder="Please select"
						onSelect={(key: number) => onAddMultiSelect(key, 'lots')}
						onDeselect={(key: number) => onRemoveMultiSelect(key, 'lots')}>
						{lots.map((item: ILot) => (
							<Option key={item.id} value={item.id}>
								{item.unit || '-'}
							</Option>
						))}
					</Select>
				</Form.Item>
				<Form.Item name="users" label="Employees">
					<Select
						mode="multiple"
						disabled
						style={{ width: '100%' }}
						placeholder="Please select"
						onSelect={(key: number) => onAddMultiSelect(key, 'users')}
						onDeselect={(key: number) => onRemoveMultiSelect(key, 'users')}>
						{users.map((item: IUser) => (
							<Option key={item.id} value={item.id}>
								{item.name}
							</Option>
						))}
					</Select>
				</Form.Item>
				<Form.Item label={`Logotype (${logotype ? 1 : 0}/1)`}>
					{isLogotypeLoading && <S.Loader />}
					<input
						type="file"
						ref={logotypeInputRef}
						name="logotype"
						onChange={onUploadLogotype}
						style={{ display: 'none' }}
					/>
					<Button
						type="dashed"
						icon={<FileImageOutlined />}
						onClick={onUploadLogotypeClick}
						style={{ width: '100%' }}
						disabled={!!logotype}>
						Upload logotype
					</Button>
					<S.Images>
						{!!logotype && (
							<S.Image>
								<S.Remove onClick={onRemoveLogotype}>
									<CloseOutlined
										style={{
											fontSize: '8px',
											color: '#000',
											width: '8px',
											display: 'block',
										}}
									/>
								</S.Remove>
								<Image
									src={logotype.url}
									alt={logotype.fileName}
									width={84}
									height={84}
									style={{ objectFit: 'cover' }}
								/>
							</S.Image>
						)}
					</S.Images>
				</Form.Item>
			</Form>
		</Drawer>
	);
};

export default EditOrganizationModal;
